import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Menu, LayoutDashboard, Database } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ThemePreset } from '../theme/presets'
import { dashboardServiceApi, WidgetQueryFilters } from '../api/dashboardService'
import { KpiCards } from '../components/Tav3Dashboard/KpiCards'
import { FilterBar, DateFilter, InfiscalMode } from '../components/Tav3Dashboard/FilterBar'
import { TabBar, PageInfo } from '../components/Tav3Dashboard/TabBar'
import { LineChart } from '../components/Tav3Dashboard/LineChart'
import { BarChart } from '../components/Tav3Dashboard/BarChart'
import { AnalyticsTable } from '../components/Tav3Dashboard/AnalyticsTable'

interface OutletContextType {
  sidebarOpen: boolean
  setSidebarOpen: (val: boolean) => void
  currentPalette: ThemePreset
}

function getDefaultDateRange(): DateFilter {
  const now = new Date()
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000)
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  }
}

function buildFilters(dateFilter: DateFilter, infiscalMode: InfiscalMode): WidgetQueryFilters {
  const f: WidgetQueryFilters = {}
  if (dateFilter.from || dateFilter.to) {
    f.dtref = {
      from: dateFilter.from || null,
      to: dateFilter.to || null,
    }
  }
  if (infiscalMode) {
    f.infiscal = infiscalMode
  }
  return f
}

export function Tav3Dashboard() {
  const { sidebarOpen, setSidebarOpen, currentPalette } = useOutletContext<OutletContextType>()
  const queryClient = useQueryClient()

  const [dateFilter, setDateFilter] = useState<DateFilter>(getDefaultDateRange)
  const [infiscalMode, setInfiscalMode] = useState<InfiscalMode>('')
  const [appliedFilter, setAppliedFilter] = useState<DateFilter>(getDefaultDateRange)
  const [appliedInfiscal, setAppliedInfiscal] = useState<InfiscalMode>('')
  const [vendedorOffset, setVendedorOffset] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [activePage, setActivePage] = useState<string>('PAGINA_1')

  const textPrimaryStyle = { color: currentPalette.primary }

  // Dashboard bundle
  const bundleQuery = useQuery({
    queryKey: ['tav3-bundle'],
    queryFn: () => dashboardServiceApi.getBundle('TAV3'),
    staleTime: 5 * 60 * 1000,
  })

  const pages: PageInfo[] = useMemo(() => {
    return (bundleQuery.data?.pages ?? []).map((p: Record<string, unknown>) => ({
      codigo: String(p.codigo ?? ''),
      nome: String(p.nome ?? ''),
      ordem: Number(p.ordem ?? 0),
    }))
  }, [bundleQuery.data])

  const currentPageInfo = useMemo(() => {
    return pages.find((p) => p.codigo === activePage) ?? pages[0]
  }, [pages, activePage])

  // Last update status
  const lastUpdateQuery = useQuery({
    queryKey: ['tav3-last-update'],
    queryFn: () => dashboardServiceApi.getLastUpdate('TAV3'),
    staleTime: 60 * 1000,
  })

  const currentFilters = buildFilters(appliedFilter, appliedInfiscal)

  // Serie valor dia (line chart)
  const serieQuery = useQuery({
    queryKey: ['tav3-serie-valor-dia', appliedFilter, appliedInfiscal],
    queryFn: () =>
      dashboardServiceApi.queryWidget('TAV3', 'SERIE_VALOR_DIA', {
        filters: currentFilters,
        limit: 400,
        offset: 0,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: !!appliedFilter.from || !!appliedFilter.to,
  })

  // Vendedor resumo (KPIs + bar chart)
  const vendedorQuery = useQuery({
    queryKey: ['tav3-vendedor-resumo', appliedFilter, appliedInfiscal, vendedorOffset],
    queryFn: () =>
      dashboardServiceApi.queryWidget('TAV3', 'VENDEDOR_RESUMO', {
        filters: currentFilters,
        limit: 50,
        offset: vendedorOffset,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: !!appliedFilter.from || !!appliedFilter.to,
  })

  // ALU Produto Cliente (datatable)
  const aluQuery = useQuery({
    queryKey: ['tav3-alu-produto-cliente', appliedFilter, appliedInfiscal],
    queryFn: () =>
      dashboardServiceApi.queryWidget('TAV3', 'ALU_PRODUTO_CLIENTE', {
        filters: currentFilters,
        limit: 200,
        offset: 0,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: !!appliedFilter.from || !!appliedFilter.to,
  })

  const cidadeQuery = useQuery({
    queryKey: ['tav3-cidade-produto', appliedFilter, appliedInfiscal],
    queryFn: () =>
      dashboardServiceApi.queryWidget('TAV3', 'CIDADE_PRODUTO', {
        filters: currentFilters,
        limit: 500,
        offset: 0,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: (!!appliedFilter.from || !!appliedFilter.to) && activePage === 'PAGINA_2',
  })

  const handleApply = useCallback(() => {
    setAppliedFilter({ ...dateFilter })
    setAppliedInfiscal(infiscalMode)
    setVendedorOffset(0)
  }, [dateFilter, infiscalMode])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await dashboardServiceApi.refreshData('TAV3', 'Refresh manual via dashboard')
      await queryClient.invalidateQueries({ queryKey: ['tav3'] })
    } catch (err) {
      console.error('Erro no refresh:', err)
    } finally {
      setRefreshing(false)
    }
  }, [queryClient])

  // KPI calculations
  const kpiData = useMemo(() => {
    const rows = vendedorQuery.data?.rows ?? []
    const totalValor = rows.reduce((acc, r) => acc + Number(r.valor ?? 0), 0)
    const totalPeso = rows.reduce((acc, r) => acc + Number(r.peso ?? 0), 0)
    const ticketMedio = totalPeso > 0 ? totalValor / totalPeso : 0

    const latest = lastUpdateQuery.data?.latest
    return {
      faturamento: totalValor,
      volumeKg: totalPeso,
      ticketMedio,
      statusCarga: {
        ultimaAtualizacao: latest?.last_success_at ?? null,
        totalLinhas: lastUpdateQuery.data?.datasets.reduce((acc, ds) => acc + ds.row_count, 0) ?? 0,
        carregando: lastUpdateQuery.isLoading,
      },
    }
  }, [vendedorQuery.data, lastUpdateQuery.data, lastUpdateQuery.isLoading])

  // Bar chart data
  const barData = useMemo(() => {
    const rows = vendedorQuery.data?.rows ?? []
    return rows
      .map((r) => ({
        label: String(r.vendedor ?? ''),
        value: Number(r.valor ?? 0),
      }))
      .sort((a, b) => b.value - a.value)
  }, [vendedorQuery.data])

  // Line chart data
  const lineData = useMemo(() => {
    const rows = serieQuery.data?.rows ?? []
    return rows.map((r) => ({
      date: String(r.dtref ?? ''),
      value: Number(r.valor ?? 0),
    }))
  }, [serieQuery.data])

  // Datatable data
  const tableData = useMemo(() => aluQuery.data?.rows ?? [], [aluQuery.data])
  const tableTotal = aluQuery.data?.pagination?.total_rows ?? 0

  const cidadeTableData = useMemo(() => cidadeQuery.data?.rows ?? [], [cidadeQuery.data])
  const cidadeTableTotal = cidadeQuery.data?.pagination?.total_rows ?? 0

  const p1Drilldown = useMemo(() => ({
    title: 'Drilldown ALU / Produto / Cliente',
    columns: ['codigo_alu', 'produto', 'cliente'],
    levelLabels: ['ALU', 'Produto', 'Cliente'],
    levelBadges: ['ALU', null, null],
  }), [])

  const p2Drilldown = useMemo(() => ({
    title: 'Drilldown Cidade / Produto',
    columns: ['cidade', 'produto'],
    levelLabels: ['Cidade', 'Produto'],
    levelBadges: ['CID', null],
  }), [])

  // Export XLSX
   const handleExport = useCallback(async () => {
    try {
      const blob = await dashboardServiceApi.exportData('TAV3', 'PAGINA_1_MATRIZ_XLSX', currentFilters)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tav3_pagina_1_matriz.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (_err) {
      // Silenciosamente
    }
  }, [currentFilters])

  // Set default dates on mount
  useEffect(() => {
    handleApply()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* SUB-HEADER */}
      <div className="h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 shadow-sm shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5" style={textPrimaryStyle} />
            <span className="text-lg font-semibold text-slate-800 dark:text-white">
              TAV3 - {currentPageInfo?.nome ?? 'Dashboard'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {bundleQuery.data && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${currentPalette.primary}15`, color: currentPalette.primary }}>
              {bundleQuery.data.dashboard.nome}
            </span>
          )}
          {lastUpdateQuery.data?.latest && (
            <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
              <Database className="w-3 h-3" />
              Última carga:{' '}
              {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
                new Date(lastUpdateQuery.data.latest.last_success_at)
              )}
            </span>
          )}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-auto p-6 scrollbar-standard">
        <div className="max-w-screen-2xl mx-auto space-y-5">
          <TabBar
            pages={pages}
            activePage={activePage}
            onPageChange={setActivePage}
            palette={currentPalette}
          />

          <FilterBar
            filter={dateFilter}
            infiscalMode={infiscalMode}
            vendedor=""
            vendedorOptions={[]}
            onVendedorChange={() => {}}
            onChange={setDateFilter}
            onInfiscalChange={setInfiscalMode}
            onApply={handleApply}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            loading={vendedorQuery.isFetching || serieQuery.isFetching || aluQuery.isFetching || cidadeQuery.isFetching}
            palette={currentPalette}
          />

          {activePage === 'PAGINA_1' ? (
            <>
              <KpiCards
                data={kpiData}
                loading={vendedorQuery.isLoading}
                palette={currentPalette}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <LineChart
                  data={lineData}
                  loading={serieQuery.isLoading}
                  palette={currentPalette}
                />
                <BarChart
                  data={barData}
                  loading={vendedorQuery.isLoading}
                  palette={currentPalette}
                />
              </div>

              <AnalyticsTable
                data={tableData}
                totalRows={tableTotal}
                loading={aluQuery.isLoading}
                palette={currentPalette}
                drilldown={p1Drilldown}
                onExport={handleExport}
              />
            </>
          ) : (
            <>
              <KpiCards
                data={kpiData}
                loading={vendedorQuery.isLoading}
                palette={currentPalette}
              />

              <AnalyticsTable
                data={cidadeTableData}
                totalRows={cidadeTableTotal}
                loading={cidadeQuery.isLoading}
                palette={currentPalette}
                drilldown={p2Drilldown}
              />
            </>
          )}
        </div>
      </main>
    </div>
  )
}
